import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Buildings, FloppyDisk, MagnifyingGlass, PencilSimple, DownloadSimple, UploadSimple } from '@phosphor-icons/react';
import { SkeletonBlock, EmptyState } from '../../components/module-ui';
import { ModalShell } from '../assets/ui';
import {
  listEconomicsProducts, listEconomicsClients, updateClientEconomics,
  downloadClientEconomicsTemplate, importClientEconomicsFile,
} from './api';

const money = (n) => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
  } catch {
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
  }
};

function Field({ label, children, wide }) {
  return (
    <div style={{ marginBottom: '8px', gridColumn: wide ? '1 / -1' : undefined }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px' }}>{label}</label>
      {children}
    </div>
  );
}

function amcMonthly(amc) {
  return (Number(amc) || 0) / 12;
}

function emptyForm(c) {
  return {
    billing_amount: c?.billing_amount ?? '',
    billing_currency: c?.billing_currency || 'INR',
    po_number: c?.po_number || '',
    po_date: (c?.po_date || '').toString().slice(0, 10),
    amc_amount: c?.amc_amount ?? '',
    billing_notes: c?.billing_notes || '',
  };
}

export default function ClientEconomicsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [productId, setProductId] = useState(null);
  const [pack, setPack] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [importMsg, setImportMsg] = useState(null);

  const refreshProducts = useCallback(() => {
    setLoading(true);
    listEconomicsProducts()
      .then((d) => setProducts(d.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refreshProducts(); }, [refreshProducts]);

  const loadClients = useCallback((pid, { silent } = {}) => {
    if (!pid) return;
    if (!silent) {
      setClientsLoading(true);
      setSelectedId(null);
      setForm(null);
      setMsg(null);
    }
    listEconomicsClients(pid)
      .then(setPack)
      .catch(() => setPack({ clients: [] }))
      .finally(() => { if (!silent) setClientsLoading(false); });
  }, []);

  useEffect(() => {
    if (productId) loadClients(productId);
  }, [productId, loadClients]);

  const filtered = products.filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return `${p.name} ${p.key || ''}`.toLowerCase().includes(s);
  });

  const openClient = (c) => {
    setSelectedId(c.client_id);
    setForm(emptyForm(c));
    setMsg(null);
  };

  const closeClient = () => {
    setSelectedId(null);
    setForm(null);
    setMsg(null);
  };

  const previewMonthly = useMemo(() => amcMonthly(form?.amc_amount), [form]);

  const save = async (e) => {
    e.preventDefault();
    if (!selectedId || !form) return;
    setSaving(true);
    setMsg(null);
    try {
      await updateClientEconomics(selectedId, {
        billing_amount: form.billing_amount === '' ? null : Number(form.billing_amount),
        billing_period: 'one_time',
        billing_currency: form.billing_currency || 'INR',
        po_number: form.po_number || null,
        po_date: form.po_date || null,
        amc_amount: form.amc_amount === '' ? null : Number(form.amc_amount),
        billing_notes: form.billing_notes || null,
      });
      closeClient();
      loadClients(productId, { silent: true });
      refreshProducts();
    } catch (err) {
      setMsg(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onImport = async (file) => {
    if (!file) return;
    setImportMsg('Importing…');
    try {
      const res = await importClientEconomicsFile(file);
      setImportMsg(`Updated ${res.updated} · skipped ${res.skipped}${res.errors?.length ? ` · ${res.errors.length} errors` : ''}`);
      refreshProducts();
      if (productId) loadClients(productId, { silent: true });
    } catch (err) {
      setImportMsg(err?.response?.data?.detail || 'Import failed');
    }
  };

  const clients = pack?.clients || [];
  const selected = clients.find((c) => c.client_id === selectedId);

  return (
    <div>
      <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 12px' }}>
        PO is one-time (implementation / license). AMC is the yearly recurring fee that renews.
        Monthly run-rate = yearly AMC ÷ 12. PO is not spread across months.
      </p>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
        <button type="button" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
          onClick={() => downloadClientEconomicsTemplate().catch((e) => alert(e.message))}>
          <DownloadSimple size={14} /> Download template
        </button>
        <label className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', margin: 0 }}>
          <UploadSimple size={14} /> Import .xlsx
          <input type="file" accept=".xlsx,.xls" hidden onChange={(e) => { onImport(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {importMsg ? <span style={{ fontSize: '12px', color: '#6B7280' }}>{importMsg}</span> : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: '14px', minHeight: '520px' }}>
        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '4px 8px', marginBottom: '8px' }}>
            <MagnifyingGlass size={14} color="#9CA3AF" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products…"
              style={{ border: 'none', outline: 'none', fontSize: '12px', flex: 1 }}
            />
          </div>
          {loading ? <SkeletonBlock rows={6} /> : filtered.length === 0 ? (
            <EmptyState icon={Buildings} title="No products" description="Products from Product Management appear here." />
          ) : (
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filtered.map((p) => (
                <button
                  key={p.product_id}
                  type="button"
                  onClick={() => setProductId(p.product_id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    background: productId === p.product_id ? '#EFF6FF' : 'transparent', marginBottom: '2px',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>
                    {p.clients_with_billing}/{p.client_count} billed · {money(p.revenue_monthly)}/mo
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px', overflow: 'auto' }}>
          {!productId ? (
            <EmptyState icon={Buildings} title="Select a product" description="Then enter the one-time PO and yearly recurring AMC for each client." />
          ) : clientsLoading ? (
            <SkeletonBlock rows={8} />
          ) : (
            <>
              <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>
                {pack?.product_name || 'Clients'}
              </h3>
              {clients.length === 0 ? (
                <p style={{ fontSize: 12, color: '#9CA3AF' }}>No clients on this product yet. Add them in Product Management.</p>
              ) : (
                <>
                  <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 10px' }}>
                    Click a client to update the one-time PO and yearly AMC.
                  </p>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>PO (one-time)</th>
                          <th>AMC (yearly, recurring)</th>
                          <th>Monthly (AMC ÷ 12)</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.map((c) => (
                          <tr
                            key={c.client_id}
                            onClick={() => openClient(c)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td style={{ fontWeight: 600 }}>{c.name}</td>
                            <td>
                              {c.billing_amount != null ? money(c.billing_amount) : 'Not set'}
                              {c.po_number ? (
                                <div style={{ fontSize: 11, color: '#6B7280' }}>{c.po_number}</div>
                              ) : null}
                            </td>
                            <td>
                              {c.amc_amount != null ? money(c.amc_amount) : 'Not set'}
                            </td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(c.revenue_monthly)}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-secondary"
                                style={{ padding: '4px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                onClick={(e) => { e.stopPropagation(); openClient(c); }}
                                aria-label={`Update PO and AMC for ${c.name}`}
                              >
                                <PencilSimple size={13} /> Update
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {selected && form ? (
        <ModalShell
          title={`${selected.name} — PO & AMC`}
          onClose={closeClient}
          testid="client-po-modal"
        >
          <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 12px' }}>
            The PO is raised once (implementation, license, go-live). AMC renews every year and is the monthly run-rate (÷ 12).
          </p>
          {selected.amc_amount == null && ['monthly', 'yearly'].includes(String(selected.billing_period || '')) ? (
            <p style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', margin: '0 0 12px' }}>
              This client had a recurring PO before AMC existed. Put the yearly maintenance in AMC so monthly run-rate stays correct after save.
            </p>
          ) : null}
          <form onSubmit={save}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
              <Field label="PO number">
                <input className="input-field" value={form.po_number}
                  onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                  autoFocus />
              </Field>
              <Field label="PO date">
                <input type="date" className="input-field" value={form.po_date}
                  onChange={(e) => setForm({ ...form, po_date: e.target.value })} />
              </Field>
              <Field label="PO amount (one-time)">
                <input type="number" step="0.01" min="0" className="input-field" value={form.billing_amount}
                  onChange={(e) => setForm({ ...form, billing_amount: e.target.value })}
                  placeholder="Implementation / license" />
              </Field>
              <Field label="AMC (yearly, recurring)">
                <input type="number" step="0.01" min="0" className="input-field" value={form.amc_amount}
                  onChange={(e) => setForm({ ...form, amc_amount: e.target.value })}
                  placeholder="Renews each year" />
              </Field>
              <Field label="Currency">
                <input className="input-field" value={form.billing_currency}
                  onChange={(e) => setForm({ ...form, billing_currency: e.target.value.toUpperCase() })} />
              </Field>
            </div>
            <div style={{
              background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8,
              padding: '10px 12px', margin: '4px 0 10px', fontSize: 12,
            }}>
              <div>
                One-time PO:{' '}
                <strong style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {form.billing_amount === '' ? '—' : money(form.billing_amount)}
                </strong>
                <span style={{ color: '#6B7280' }}> — not in monthly run-rate</span>
              </div>
              <div style={{ marginTop: 4 }}>
                Monthly run-rate:{' '}
                <strong style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(previewMonthly)}</strong>
                <span style={{ color: '#6B7280' }}>
                  {' '}(yearly AMC {form.amc_amount === '' ? '—' : money(form.amc_amount)} ÷ 12)
                </span>
              </div>
            </div>
            <Field label="Notes / contract ref" wide>
              <textarea className="input-field" rows={2} value={form.billing_notes}
                onChange={(e) => setForm({ ...form, billing_notes: e.target.value })}
                placeholder="SOW, contract clause, invoice notes…" />
            </Field>
            {selected.billing_updated_by_name ? (
              <p style={{ fontSize: 11, color: '#9CA3AF', margin: '0 0 8px' }}>
                Last updated by {selected.billing_updated_by_name}
              </p>
            ) : null}
            {msg ? <p style={{ fontSize: 12, color: '#B91C1C', margin: '0 0 8px' }}>{msg}</p> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button type="submit" className="btn-primary" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FloppyDisk size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn-secondary" onClick={closeClient} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </ModalShell>
      ) : null}
    </div>
  );
}
